import moment from "moment";
import { Link } from "react-router-dom";
import type { Job } from "@models/Job";
import { getConsultantName } from "./ApplicantList";
import HoverPopover from "@components/HoverPopover";

/** Lines of the job description shown before it is cut off */
const DESCRIPTION_LINES = 4;

interface Props {
	orderNumber: string;
	/** Loaded separately from the schedule, undefined while loading */
	details?: Job;
	/** Link to the job's own schedule, for jobs that can still be changed */
	extendHref?: string;
	extendLabel?: string;
	children: React.ReactNode;
}

/**
 * Shows when and by whom a job was created and the start of its description
 * while its number is hovered. Moving the pointer onto the popover keeps it open.
 */
const JobInfoPopover = ({ orderNumber, details, extendHref, extendLabel = "Förläng uppdrag", children }: Props) => {
	const created = details?.metadata?.created;
	const createdBy = details?.metadata?.createdBy;

	return (
		<HoverPopover
			className="w-80"
			content={
				<>
					<div className="text-sm font-semibold text-gray-900">Uppdrag #{orderNumber}</div>
					{!details && <div className="mt-1 text-sm text-gray-500">Hämtar…</div>}
					{details && (
						<>
							{created && (
								<div className="mt-0.5 text-xs text-gray-500">
									Skapat {moment(created).locale("sv").format("D MMM YYYY HH:mm")}
									{createdBy?.firstName && ` av ${getConsultantName(createdBy)}`}
								</div>
							)}
							{/* Same label as on the job page, so it's clear which text this is */}
							<div className="mt-2 text-xs text-gray-500">Arbetsbeskrivning</div>
							<div
								className="mt-0.5 whitespace-pre-wrap text-sm text-gray-700"
								style={{
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: DESCRIPTION_LINES,
									overflow: "hidden",
								}}
							>
								{details.description || <span className="italic text-gray-500">Ingen beskrivning</span>}
							</div>
						</>
					)}
					{extendHref && (
						<Link to={extendHref} className="mt-2 inline-block text-sm font-medium !text-[#219CD0] hover:underline">
							{extendLabel}
						</Link>
					)}
				</>
			}
		>
			{children}
		</HoverPopover>
	);
};

export default JobInfoPopover;
